import { isEmberTesting } from './is-testing';
import { pdfRuntimeAssetUrl, pdfRuntimeFileUrl } from './pdf-runtime-urls';
import withHiddenArraySuper from './with-hidden-array-super';

const pdfLoadIds = new WeakMap();

function isFastBoot() {
  return typeof globalThis.FastBoot !== 'undefined';
}

function normalizeDocumentSource(source) {
  if (typeof source === 'string' || source instanceof URL) {
    return { url: source };
  }

  if (source instanceof ArrayBuffer || ArrayBuffer.isView(source)) {
    return { data: source };
  }

  return source;
}

function documentOptions(source) {
  let options = normalizeDocumentSource(source);

  return {
    disableStream: isEmberTesting() || options.disableStream,
    iccUrl: pdfRuntimeAssetUrl('iccs'),
    wasmUrl: pdfRuntimeAssetUrl('wasm'),
    ...options,
    isEvalSupported: false,
  };
}

function setupPdf() {
  let loadPdfJsLib = async function () {
    // FastBoot has no canvas/worker document. Skip the dynamic import rather
    // than server-rendering PDFs. Do not stub `document` in the FastBoot
    // sandbox to paper over a missing global — jQuery then calls
    // `createElement` on the stub and SSR still 500s.
    //
    // Do not resolve the worker/library with `new URL(..., import.meta['url'])`.
    // Webpack treats that as an asset module and emits
    // `__webpack_require__.b = document.baseURI || self.location.href`,
    // which throws in FastBoot (no global document).
    if (isFastBoot()) {
      return null;
    }

    // pdfjs-dist 5.x ships a pre-bundled ESM build that already contains a
    // nested webpack runtime (`__webpack_exports__`). Embroider/Webpack with
    // an `eval*` devtool re-wraps that file and the inner `var
    // __webpack_exports__` shadows the outer module exports, so
    // `__webpack_require__.r(...)` throws "Object.defineProperty called on
    // non-object" (webpack#20095). Serve the file as a public asset and import
    // it as native ESM so the host bundler never rewrites it.
    let pdfJsUrl = pdfRuntimeFileUrl('legacy/build', 'pdf.mjs');
    let workerSrc = pdfRuntimeFileUrl('legacy/build', 'pdf.worker.min.mjs');

    let pdfJsLib = await import(/* webpackIgnore: true */ pdfJsUrl);
    pdfJsLib.GlobalWorkerOptions.workerSrc = workerSrc;

    return pdfJsLib;
  };

  let getDocument = async function (source, pdfjsLib) {
    let lib = pdfjsLib ?? (await loadPdfJsLib());

    if (!lib) {
      throw new Error();
    }

    return lib.getDocument(documentOptions(source)).promise;
  };

  let renderPdf = async function (pdfjsLib, url, container) {
    if (isFastBoot() || !pdfjsLib) {
      throw new Error();
    }

    function renderPage(page) {
      // Double viewport is for retina
      let viewport = page.getViewport({
        scale: (container.clientWidth / page.getViewport({ scale: 1.0 }).width) * 2,
      });
      let canvas = document.createElement('canvas');
      let ctx = canvas.getContext('2d');
      let renderContext = {
        canvasContext: ctx,
        viewport,
      };

      canvas.height = viewport.height;
      canvas.width = viewport.width;
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      container.appendChild(canvas);

      return page.render(renderContext).promise;
    }

    /* eslint-disable promise/prefer-await-to-then */
    async function renderPages(doc) {
      let promises = Array.apply(null, Array(doc.numPages)).map(async function (_, i) {
        return await doc.getPage(i + 1);
      });

      return Promise.all(promises).then(async pages => {
        for (let page of pages) {
          await renderPage(page);
        }
      });
    }

    let doc = await getDocument(url, pdfjsLib);
    return renderPages(doc);
  };

  let printPdf = function (url) {
    // NOTE: window.open/print freezes headless Chrome in Ember browser tests
    // (same class of hang as Safari). Keep the API contract (`false`) without
    // opening a print dialog while QUnit is driving the page.
    if (isFastBoot() || isEmberTesting()) {
      return false;
    }

    let pdfWindow = window.open(url);

    // NOTE: calling print disabled for Safari because it just freezes
    if (window.safari !== undefined) {
      return;
    }

    // eslint-disable-next-line ember-cleanup/no-settimeout
    setTimeout(function () {
      if (pdfWindow) {
        pdfWindow.print();
      }
    }, 100);

    return false;
  };

  function setPdfPromise(target, value) {
    if (typeof target.set === 'function') {
      target.set('pdfPromise', value);
      return;
    }

    target.pdfPromise = value;
  }

  function isCurrentPdfLoad(target, loadId) {
    return pdfLoadIds.get(target) === loadId;
  }

  // Shared by SimplePractice and Client Portal. PDF.js 5 rejects with a native
  // ResponseException; Ember Data's PromiseObject rethrows that on a detached
  // then(), which QUnit reports as a global failure. Drive spinner/banner
  // flags ourselves so the native rejection stays handled. Fire-and-forget
  // `.then()` — callers must not await this (payment afterPersist must not
  // wait on render).
  let loadRenderedPdf = function (target, source, container) {
    if (isFastBoot()) {
      return;
    }

    let loadId = (pdfLoadIds.get(target) ?? 0) + 1;
    pdfLoadIds.set(target, loadId);

    return withHiddenArraySuper(() => {
      setPdfPromise(target, { isPending: true, isRejected: false });

      /* eslint-disable promise/prefer-await-to-then */
      return loadPdfJsLib()
        .then(pdfjsLib => renderPdf(pdfjsLib, source, container))
        .then(() => {
          if (target.isDestroyed || target.isDestroying || !isCurrentPdfLoad(target, loadId)) {
            return;
          }
          setPdfPromise(target, { isPending: false, isRejected: false });
        })
        .catch(() => {
          if (target.isDestroyed || target.isDestroying || !isCurrentPdfLoad(target, loadId)) {
            return;
          }
          setPdfPromise(target, { isPending: false, isRejected: true });
        });
    });
  };

  return {
    renderPdf,
    printPdf,
    loadPdfJsLib,
    getDocument,
    loadRenderedPdf,
  };
}

export default setupPdf;
